import * as React from 'react'

// How long a saved pick waits for the record to report it before the record shows again.
export const RECORD_REPORT_TIMEOUT_MS = 10_000

interface IOptimisticCellValue<T> {
  /** The picked value while it is being saved, then the saved one. */
  shownValue: T
  /** True from a pick until its save settles; no other pick starts meanwhile. */
  isSaving: boolean
  /** Saves `next` unless it is already shown or another save is in flight. */
  commit: (next: T) => Promise<void>
}

/**
 * One save at a time for a cell editing a server value. Overlapping saves could land out of
 * order and leave an older pick as the final value, so a pick made while one is in flight is
 * ignored.
 *
 * The pick shows until the record catches up. Records carry no revision here, so the cell
 * tells its own late echoes from other updates by remembering the picks the record has not
 * reported yet. The latest pick arriving hands the cell back to the record - at once, or when
 * its save settles - even when the same value was picked earlier too: a repeated pick's echo
 * can't be told apart, and an older echo replayed after it only shows until the next update.
 * An earlier pick arriving is a late echo (it and every pick before it count as reported).
 * Any other value is someone else's: newer than the pick once its save has settled, so the
 * record shows it; older while the save is still in flight, so it is ignored.
 * A failed save, or no report within RECORD_REPORT_TIMEOUT_MS of a saved pick, shows the
 * record too, so the cell can never stay stale.
 */
export function useOptimisticCellValue<T>(
  value: T,
  save: (next: T) => Promise<void>,
  onError: (error: unknown) => void,
): IOptimisticCellValue<T> {
  const [pending, setPending] = React.useState<{ value: T } | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const isSavingRef = React.useRef(false)
  const valueRef = React.useRef(value)
  valueRef.current = value
  // Picks sent but not yet reported by the record, oldest first.
  const unreportedPicksRef = React.useRef<T[]>([])
  // The latest pick was reported while its save was still in flight.
  const isReportedRef = React.useRef(false)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  // A virtualized row can scroll away mid-save.
  const isMountedRef = React.useRef(true)

  const clearReportTimeout = () => {
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }

  const release = () => {
    clearReportTimeout()
    unreportedPicksRef.current = []
    if (isMountedRef.current) setPending(null)
  }

  React.useEffect(
    () => () => {
      isMountedRef.current = false
      clearReportTimeout()
    },
    [],
  )

  React.useEffect(() => {
    const picks = unreportedPicksRef.current

    if (picks.length > 0 && value === picks[picks.length - 1]) {
      picks.length = 0
      if (isSavingRef.current) isReportedRef.current = true
      else release()
      return
    }

    const index = picks.indexOf(value)
    if (index === -1) {
      if (!isSavingRef.current) release()
      return
    }

    picks.splice(0, index + 1)
  }, [value])

  const shownValue = pending ? pending.value : value

  const commit = async (next: T) => {
    if (isSavingRef.current || next === shownValue) return

    isSavingRef.current = true
    isReportedRef.current = valueRef.current === next
    clearReportTimeout()
    unreportedPicksRef.current.push(next)
    setIsSaving(true)
    setPending({ value: next })
    try {
      await save(next)
      if (isReportedRef.current) {
        release()
      } else if (isMountedRef.current) {
        timeoutRef.current = setTimeout(release, RECORD_REPORT_TIMEOUT_MS)
      }
    } catch (error) {
      release()
      onError(error)
    } finally {
      isSavingRef.current = false
      if (isMountedRef.current) setIsSaving(false)
    }
  }

  return { shownValue, isSaving, commit }
}
